import { useLocation, useSearchParams } from "react-router-dom";
import { createRef, useCallback, useContext, useEffect, useState } from "react";
import { AppContext } from "../../../../App";
import { useAllTransactionsFilterNew } from "../../AllTransactions/components/AllTransactionsFilter/hooks";
import shortid from "shortid";
import CustomTable from "../../../CustomTable";
import {
  getRecords,
  handleDownloadExcelGridData,
} from "../../AllTransactions/services";
import { getUserColumnConfiguration } from "../../ColumnsConfiguration/column-configuration.service";
import { createArrayOfColumnNames } from "../../ColumnsConfiguration/column-configuration.middleware";
import { verifyFiles } from "../../../../services/missingCode";
import moment from "moment/moment";
import { toast } from "react-toastify";
import { manageDownloadDocument } from "../../../../util/download-file";

const _setDateFilters = (filters) => {
  return {
    currentDate: filters.startDate ? new Date(filters.startDate) : new Date(),
    dateRange:
      filters.startDate && filters.endDate && filters.hasFilter
        ? {
            startDate: new Date(filters.startDate),
            endDate: new Date(filters.endDate),
          }
        : {},
  };
};

const baseColumns = [
  "Appointment",
  "Patient Name",
  "Practice",
  "Type",
  "Carrier Name",
  "Missing Fields",
  "Verification By",
];

export const useTransactionSegments = (permissions, selectedPractices) => {
  const _permissions = {
    missing_code: {
      edit_missingcode: false,
    },
    ...permissions,
  };
  const [, setSearchParams] = useSearchParams();
  const appContext = useContext(AppContext);
  const { transaction: sortTransaction } = appContext.get("sortConfiguration");
  const [filters, _setFilters] = useAllTransactionsFilterNew();
  const [loaded, setLoaded] = useState(false);
  const [dateFilters, setDateFilters] = useState(_setDateFilters(filters));
  const [key, setKey] = useState(shortid.generate());
  const [userColumns, setUserColumns] = useState([]);
  const [showModal, setShowModal] = useState(null);
  const [itemVerification, setItemVerification] = useState({});
  const [files, setFiles] = useState(null);
  const [showModalExport, setShowModalExport] = useState(false);
  const [isDownloadingFile, setIsDownloadingFile] = useState(false);
  const [isUpdatedFilters, setIsUpdatedFilters] = useState(false);
  const practices =
    JSON.parse(localStorage.getItem("appSettings")).practices || [];

  let location = useLocation();

  const ref = createRef(null);

  const setFilters = useCallback(
    (f) => {
      const { form, range = false, ...rest } = f;
      if (form) {
        setDateFilters({
          ...dateFilters,
          currentDate: f.startDate || new Date(),
          dateRange: {
            startDate: f.startDate,
            endDate: f.endDate,
          },
        });
      }
      if (!range) {
        setDateFilters({
          ...dateFilters,
          currentDate: f.startDate || new Date(),
          dateRange: {},
        });
      }

      setIsUpdatedFilters(true);
      //setCurrentPage(1)
      _setFilters(rest);
    },
    [filters],
  );

  const handleSetFilters = (f) => {
    setSearchParams({
      page: 0,
    });
    setDateFilters({
      ...dateFilters,
      currentDate: f.startDate || new Date(),
    });
    localStorage.setItem("filters", JSON.stringify(f));
    _setFilters(f);
  };

  const getData = useCallback(
    ({ page, pageSize }) => {
      function response(records = [], quantity = 0) {
        return CustomTable.createTableResponse(records, page, quantity);
      }

      if (selectedPractices.length === 0)
        return new Promise((resolve) => {
          resolve(response());
        });

      return getRecords(
        {
          ...filters,
          practiceId: selectedPractices,
          offset: isUpdatedFilters ? 0 : page ? pageSize * page : 0,
          filter: {
            ...filters.filter,
            ivProcessResult: {
              operation: "nc",
              value: "Done",
            },
          },
          sortOptions: {
            practiceName: 1,
            dateToOrder: 1,
          },
        },
        pageSize,
      ).then(([status, r]) => {
        if (!status) {
          return response([], 0);
        }

        const formattedRecords =
          r && r.records.length > 0
            ? r.records.map((i) => {
                i.type = i.type.toUpperCase();
                return i;
              })
            : [];

        setIsUpdatedFilters(false);
        return response(formattedRecords, r.recordsQty || 0);
      });
    },
    [selectedPractices, filters, sortTransaction],
  );

  useEffect(() => {
    if (!loaded) {
      setLoaded(true);
    }
    if (ref.current != null && filters) {
      ref.current.onQueryChange();
    }
  }, [filters, selectedPractices]);

  const refresh = () => {
    if (ref.current != null && filters) {
      ref.current.onQueryChange();
    }
  };

  useEffect(() => {
    setKey(shortid.generate());
  }, [filters, sortTransaction]);

  useEffect(() => {
    async function userColumns() {
      const columns = await getUserColumnConfiguration("");
      const [, columnsResponse] = columns;
      if (columnsResponse.length > 0) {
        setUserColumns(createArrayOfColumnNames(columnsResponse));
      } else {
        setUserColumns(baseColumns);
      }
    }
    userColumns();
  }, [location]);

  async function handleVerify(id, practice) {
    const [status] = await verifyFiles(id, practice);

    if (status) {
      setFiles(id);
      setShowModal(true);
    }
  }

  async function downloadGridData() {
    setIsDownloadingFile(true);
    const request = {
      ...filters,
      practiceId: selectedPractices,
      startDate: moment(filters.startDate, "YYYY-MM-DD").format("YYYY-MM-DD"),
      endDate: moment(filters.endDate, "YYYY-MM-DD").format("YYYY-MM-DD"),
      filter: {
        ...filters.filter,
        ivProcessResult: {
          operation: "nc",
          value: "Done",
        },
      },
      sortOptions: {
        practiceName: 1,
        dateToOrder: 1,
      },
    };

    await handleDownloadExcelGridData({
      ...request,
      filter: cleanObject(request.filter),
    })
      .then((response) => {
        downloadFile(response);
        setTimeout(() => {
          setIsDownloadingFile(false);
          setShowModalExport(false);
        }, 1000);
      })
      .catch(() => {
        showErrorAndHideLoadingFile();
      });
  }

  const showErrorAndHideLoadingFile = () => {
    toast.error("error downloading the file, try again");
    setIsDownloadingFile(false);
  };

  function downloadFile(response) {
    const practice =
      selectedPractices.length > 0
        ? practices.find((practice) => practice._id === selectedPractices[0])
        : "";

    manageDownloadDocument(
      `${practice.client[0].name || ""} - ${moment().format(
        "DD-MM-YYYY HH:mm:ss",
      )}`,
      response,
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    );
  }

  function cleanObject(obj) {
    const cleanedEntries = Object.entries(obj).filter(([_, value]) => {
      // Caso 1: object with operation and value
      if (
        typeof value === "object" &&
        value !== null &&
        "operation" in value &&
        "value" in value
      ) {
        return value.operation.trim() !== "" || value.value.trim() !== "";
      }

      // Caso 2: single value (e.g., patientId)
      return value !== "" && value !== null && value !== undefined;
    });

    return Object.fromEntries(cleanedEntries);
  }

  function openModalVerification(item) {
    setItemVerification(item);
    setShowModal(true);
  }

  return {
    _permissions,
    sortTransaction,
    filters,
    loaded,
    dateFilters,
    ref,
    setFilters,
    handleSetFilters,
    getData,
    refresh,
    key,
    userColumns,
    openModalVerification,
    showModalExport,
    setShowModalExport,
    downloadGridData,
    isDownloadingFile,
    itemVerification,
  };
};
